import React, { useEffect, useState, useContext } from "react";
import { Drawer, Form, Input, Checkbox, Row, Col, Upload, Button, Space, Typography, Card, Spin, Divider, Switch, Tooltip, Select, Tag, Popover } from "antd";
import { PlusOutlined, DeleteOutlined, DownloadOutlined, LoadingOutlined, CloseOutlined, PaperClipOutlined, TagOutlined } from "@ant-design/icons";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import TicketAlertTemplatesService from "../../Services/TicketAlertTemplatesService";
import { adminContext } from "../../Common/PageRoute";
// @ts-ignore: allow side-effect import of CSS in TypeScript file
import "./TicketAlertTemplates.css";

const { Text } = Typography;

interface AlertEventDefinition {
  id: string;
  name: string;
  description?: string;
  collection?: string;
  tokens?: Array<string | AlertTemplateToken>;
}

interface AlertTemplateToken {
  key: string;
  label?: string;
  description?: string;
}

export interface TicketAlertTemplatesDetailsPanelProps {
  visible: boolean;
  record?: any;
  onClose: (confirmed: boolean) => void;
}

const TicketAlertTemplatesDetailsPanel: React.FC<TicketAlertTemplatesDetailsPanelProps> = ({
  visible,
  record,
  onClose,
}) => {
  const [form] = Form.useForm();
  const { openNotification } = useContext(adminContext);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [alertEventsLoading, setAlertEventsLoading] = useState(false);
  const [alertEvents, setAlertEvents] = useState<AlertEventDefinition[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [editorInstance, setEditorInstance] = useState<any>(null);
  const [searchText, setSearchText] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const notificationEnabled = Form.useWatch(["notification", "isEnabled"], form);
  const selectedEventKey = Form.useWatch(["notification", "alertEventKey"], form);
  const selectedEvent = alertEvents.find((item) => item.id === selectedEventKey);
  const isClientEvent = selectedEvent?.collection === "Client";

  // // NEED TO HANDLE IN BETTER WAY
  // useEffect(() => {
  //   // When the selected event changes to a non-Client event, clear client-only flags
  //   if (!isClientEvent) {
  //     debugger
  //     form.setFieldsValue({
  //       notification: {
  //         includeAccountManager: false,
  //         includeAccountant: false,
  //         includeFirstResponse: false,
  //       },
  //     });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [selectedEventKey]);

  useEffect(() => {
    if (visible) {
      if (alertEvents.length === 0) {
        setAlertEventsLoading(true);
        TicketAlertTemplatesService.getAlertEvents()
          .then((res: any) => {
            if (res?.status) {
              setAlertEvents(res.result ?? []);
            } else {
              openNotification("error", res?.message || "Failed to fetch alert events");
            }
          })
          .catch(() => openNotification("error", "Error fetching alert events"))
          .finally(() => setAlertEventsLoading(false));
      }

      if (record?.id) {
        setFetching(true);
        TicketAlertTemplatesService.get(record.id)
          .then((res: any) => {
            if (res?.status && res?.result) {
              const data = res.result;
              form.setFieldsValue({
                name: data.name ?? "",
                subject: data.subject ?? "",
                notification: {
                  isEnabled: data.notification?.isEnabled ?? false,
                  alertEventKey: data.notification?.alertEventKey,
                  isNotifyToClient: data.notification?.isNotifyToClient ?? false,
                  defaultTo: data.notification?.defaultTo,
                  defaultCCs: mapDefaultUsers(data.notification?.defaultCCs),
                  defaultBCCs: mapDefaultUsers(data.notification?.defaultBCCs),
                  includeAccountant: data.notification?.includeAccountant ?? false,
                  includeAccountManager: data.notification?.includeAccountManager ?? false,
                  includeFirstResponse: data.notification?.includeFirstResponse ?? false,
                  includeAssignee: data.notification?.includeAssignee ?? false,
                  includeOwner: data.notification?.includeOwner ?? false,
                  includeTicketCreator: data.notification?.includeTicketCreator ?? false,
                  includeCollaborators: data.notification?.includeCollaborators ?? false,
                  includeSupportAgent: data.notification?.includeSupportAgent ?? false,
                  includeSupportCollaborators: data.notification?.includeSupportCollaborators ?? false,
                  includeSupportStaff: data.notification?.includeSupportStaff ?? false,
                  includeAllAgents: data.notification?.includeAllAgents ?? false,
                  isExcluded: data.notification?.isExcluded ?? false,
                },
                message: data.message ?? "",
              });

              setExistingAttachments(data.attachment ?? []);
            } else {
              openNotification("error", res?.message || "Failed to fetch alert template details");
              onClose(false);
            }
          })
          .catch(() => {
            openNotification("error", "Error fetching alert template details");
            onClose(false);
          })
          .finally(() => {
            setFetching(false);
          });
      } else {
        form.setFieldsValue({
          message: "",
          notification: defaultNotificationValues(),
        });
        setExistingAttachments([]);
        setNewFiles([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, record]);

  const defaultNotificationValues = () => ({
    isEnabled: false,
    alertEventKey: undefined,
    isNotifyToClient: false,
    defaultTo: "",
    defaultCCs: [],
    defaultBCCs: [],
    includeAccountant: false,
    includeAccountManager: false,
    includeFirstResponse: false,
    includeAssignee: false,
    includeOwner: false,
    includeTicketCreator: false,
    includeCollaborators: false,
    includeSupportAgent: false,
    includeSupportStaff: false,
    isExcluded: false,
  });


  const splitEmails = (value?: string) =>
    value
      ? value.split(/[;,]/).map((item) => item.trim()).filter(Boolean)
      : [];

  const mapDefaultUsers = (items?: any[]) =>
    items?.map((item) => item.email || item.name || item.value || item.id).filter(Boolean) ?? [];

  const mapEmailsToIdName = (items?: string[]) =>
    items?.filter(Boolean).map((email) => ({ name: email })) ?? [];

  const getTokenKey = (token: string | AlertTemplateToken) =>
    typeof token === "string" ? token : token.key;

  const getTokenLabel = (token: string | AlertTemplateToken) =>
    typeof token === "string" ? token : token.label || token.key;

  const handleSave = () => {
    form.validateFields()
      .then((values) => {
        setLoading(true);
        const payload: any = {
          name: values.name,
          subject: values.subject || "",
          message: values.message,
          files: [...existingAttachments, ...newFiles],
          notification: {
              isEnabled: values.notification?.isEnabled,
              alertEventKey: values.notification.alertEventKey,
              isNotifyToClient: values.notification.isNotifyToClient ?? false,
              defaultTo: values.notification.defaultTo,
              defaultCCs: mapEmailsToIdName(values.notification.defaultCCs),
              defaultBCCs: mapEmailsToIdName(values.notification.defaultBCCs),
              includeAccountant: values.notification.includeAccountant ?? false,
              includeAccountManager: values.notification.includeAccountManager ?? false,
              includeFirstResponse: values.notification.includeFirstResponse ?? false,
              includeAssignee: values.notification.includeAssignee ?? false,
              includeOwner: values.notification.includeOwner ?? false,
              includeTicketCreator: values.notification.includeTicketCreator ?? false,
              includeCollaborators: values.notification.includeCollaborators ?? false,
              includeSupportAgent: values.notification.includeSupportAgent ?? false,
              includeSupportCollaborators: values.notification.includeSupportCollaborators ?? false,
              includeAllAgents: values.notification.includeAllAgents ?? false,
              includeSupportStaff: values.notification.includeSupportStaff ?? false,
              isExcluded: values.notification.isExcluded ?? false,
            },
        };

        const id = record?.id || "";
        TicketAlertTemplatesService.post(id, payload)
          .then((res) => {
            setLoading(false);
            if (res?.status) {
              openNotification(
                "success",
                `Alert template saved successfully`
              );
              onClose(true);
            } else {
              openNotification("error", res?.message || "Failed to save alert template");
            }
          })
          .catch(() => {
            setLoading(false);
            openNotification("error", "Error saving alert template");
          });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleUpload = (file: File) => {
    setNewFiles((prev) => [...prev, file]);
    return false;
  };

  const handleRemoveNewFile = (index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingAttachment = (fileId: string) => {
    if (!record?.id) return;
    TicketAlertTemplatesService.deleteAttachment(record.id, fileId)
      .then((res) => {
        if (res?.status) {
          setExistingAttachments((prev) => prev.filter((f) => f.id !== fileId));
          openNotification("success", "Attachment deleted successfully");
        } else {
          openNotification("error", res?.message || "Failed to delete attachment");
        }
      })
      .catch(() => {
        openNotification("error", "Error deleting attachment");
      });
  };

  const handleDownloadAttachment = (fileId: string) => {
    if (!record?.id) return;
    TicketAlertTemplatesService.downloadAttachment(record.id, fileId, true);
  };

  const handleInsertTag = (tokenKey: string) => {
    if (editorInstance) {
      editorInstance.model.change((writer: any) => {
        const insertPosition = editorInstance.model.document.selection.getFirstPosition();
        if (insertPosition) {
          writer.insertText(`[${tokenKey}]`, insertPosition);
        }
      });
      editorInstance.editing.view.focus();
      form.setFieldsValue({ message: editorInstance.getData() });
    }
    setPopoverOpen(false);
    setSearchText("");
  };

  const filteredTokens = (selectedEvent?.tokens ?? []).filter((token) => {
    const label = getTokenLabel(token).toLowerCase();
    const key = getTokenKey(token).toLowerCase();
    const search = searchText.toLowerCase();
    return label.includes(search) || key.includes(search);
  });

  const popoverContent = (
    <div style={{ width: 340, padding: "8px 4px" }}>
      <Input
        placeholder="Search tags"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 12, borderRadius: 6 }}
        allowClear
        autoFocus
      />
      <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 6, padding: "4px 0" }}>
        {filteredTokens.length === 0 ? (
          <div style={{ padding: "8px 12px", color: "#8c8c8c", textAlign: "center" }}>No tags found</div>
        ) : (
          filteredTokens.map((token) => {
            const tokenKey = getTokenKey(token);
            const tokenLabel = getTokenLabel(token);
            return (
              <div
                key={tokenKey}
                className="tag-dropdown-item"
                onClick={() => handleInsertTag(tokenKey)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 12px",
                  cursor: "pointer",
                  transition: "background 0.2s",
                }}
              >
                <span style={{ fontSize: 13, color: "#262626" }}>{tokenLabel}</span>
                <span style={{ fontSize: 12, color: "#8c8c8c", fontFamily: "monospace" }}>[{tokenKey}]</span>
              </div>
            );
          })
        )}
      </div>
      <Divider style={{ margin: "12px 0 8px" }} />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={() => {
            setPopoverOpen(false);
            setSearchText("");
          }}
          style={{ borderRadius: 4 }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );

  const handleClose = () => {
    form.resetFields();
    setExistingAttachments([]);
    setNewFiles([]);
    onClose(false);
  };

  return (
    <Drawer
      title={record?.id ? `Edit ${record?.name || "alert template"}` : "Add alert template"}
      placement="right"
      width={800}
      onClose={handleClose}
      open={visible}
      destroyOnClose
      closable={true}
      styles={{ body: { overflowX: "hidden" } }}
      extra={
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={handleClose}
        />
      }
      footer={
        <div style={{ textAlign: "right" }}>
          <Space>
            <Button onClick={handleClose} disabled={loading || fetching}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSave}
              loading={loading}
              disabled={fetching}
              style={{ borderRadius: 6 }}
            >
              Save
            </Button>
          </Space>
        </div>
      }
    >
      {fetching ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </div>
      ) : (
        <div style={{ padding: "16px 24px" }}>
          <Form form={form} layout="vertical" requiredMark={false}>
            {/* Row 1: Name and Subject */}
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[{ required: true, message: "Enter template name" }]}
                  style={{ marginBottom: 20 }}
                >
                  <Input placeholder="Enter template name" style={{ borderRadius: 6 }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Subject"
                  name="subject"
                  rules={[
                    {
                      required: true,
                      message: "Enter message subject",
                    },
                  ]}
                  style={{ marginBottom: 20 }}
                >
                  <Input
                    placeholder="Enter message subject"
                    style={{ borderRadius: 6 }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: "4px 0 20px" }} />

            <Card size="small" className="ticket-alert-card" style={{ marginBottom: 24 }}>
              <Row gutter={16} align="middle">
                <Col xs={24} sm={8}>
                  <Form.Item
                    label="Alert template"
                    name={["notification", "isEnabled"]}
                    valuePropName="checked"
                    style={{ marginBottom: 16 }}
                  >
                    <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={16}>
                  <Form.Item
                    label="Alert event"
                    name={["notification", "alertEventKey"]}
                    rules={notificationEnabled ? [{ required: true, message: "Select alert event" }] : []}
                    style={{ marginBottom: 16 }}
                  >
                    <Select
                      allowClear
                      showSearch
                      disabled={!notificationEnabled}
                      loading={alertEventsLoading}
                      placeholder="Select registered event"
                      optionFilterProp="label"
                      options={alertEvents.map((item) => ({
                        value: item.id,
                        label: `${item.name} (${item.id})`,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16} style={{ marginTop: 10 }}>
                {isClientEvent ? <Col xs={24} sm={12}>
                  <Space direction="vertical" size={4}>
                    <Form.Item name={["notification", "includeAccountManager"]} valuePropName="checked" noStyle>
                      <Checkbox disabled={!notificationEnabled}>Account manager</Checkbox>
                    </Form.Item>
                    <Form.Item name={["notification", "includeAccountant"]} valuePropName="checked" noStyle>
                      <Checkbox disabled={!notificationEnabled}>Accountant</Checkbox>
                    </Form.Item>
                    <Form.Item name={["notification", "includeFirstResponse"]} valuePropName="checked" noStyle>
                      <Checkbox disabled={!notificationEnabled}>First response</Checkbox>
                    </Form.Item>
                  </Space>
                </Col> :
                  <Col xs={24} sm={12}>
                    <Space direction="vertical" size={4}>
                      <Form.Item name={["notification", "includeAllAgents"]} valuePropName="checked" noStyle>
                        <Checkbox disabled={!notificationEnabled}>All agents</Checkbox>
                      </Form.Item>
                      <Form.Item name={["notification", "includeSupportAgent"]} valuePropName="checked" noStyle>
                        <Checkbox disabled={!notificationEnabled}>Support agent</Checkbox>
                      </Form.Item>
                      <Form.Item name={["notification", "includeSupportCollaborators"]} valuePropName="checked" noStyle>
                        <Checkbox disabled={!notificationEnabled}>Support collaborators</Checkbox>
                      </Form.Item>
                    </Space>
                  </Col>
                }
                <Col xs={24} sm={12}>
                  <Space direction="vertical" size={4}>
                    <Form.Item name={["notification", "includeTicketCreator"]} valuePropName="checked" noStyle>
                      <Checkbox disabled={!notificationEnabled}>Ticket creator</Checkbox>
                    </Form.Item>
                    {isClientEvent && <Form.Item name={["notification", "includeAssignee"]} valuePropName="checked" noStyle>
                      <Checkbox disabled={!notificationEnabled}>Practice agent</Checkbox>
                    </Form.Item>}
                    <Form.Item name={["notification", "includeCollaborators"]} valuePropName="checked" noStyle>
                      <Checkbox disabled={!notificationEnabled}>Practice collaborators</Checkbox>
                    </Form.Item>
                  </Space>
                </Col>
              </Row>

              <div style={{ marginTop: 15 }}>
                <Col xs={24} sm={8}>
                  <Form.Item label="CCs" name={["notification", "defaultCCs"]} style={{ marginBottom: 16 }}>
                    <Select
                      mode="tags"
                      disabled={!notificationEnabled}
                      placeholder="Add email addresses"
                      tokenSeparators={[",", ";"]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label="BCCs" name={["notification", "defaultBCCs"]} style={{ marginBottom: 16 }}>
                    <Select
                      mode="tags"
                      disabled={!notificationEnabled}
                      placeholder="Add email addresses"
                      tokenSeparators={[",", ";"]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label="Fallback email address" name={["notification", "defaultTo"]} style={{ marginBottom: 16 }}>
                    <Input placeholder="Add email address" style={{ borderRadius: 6 }} />
                  </Form.Item>
                </Col>
              </div>

            </Card>

            {/* Message Editor */}
            <Form.Item
              label="Message"
              name="message"
              rules={[{ required: true, message: "Enter message body" }]}
              style={{ marginBottom: 24 }}
            >
              <div className="ckeditor-description">
                {selectedEvent?.tokens && selectedEvent.tokens.length > 0 && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                    <Popover
                      content={popoverContent}
                      trigger="click"
                      open={popoverOpen}
                      onOpenChange={(visible) => {
                        setPopoverOpen(visible);
                        if (!visible) setSearchText("");
                      }}
                      placement="bottomRight"
                      overlayStyle={{ padding: 0 }}
                    >
                      <Button
                        size="small"
                        icon={<TagOutlined />}
                        style={{ borderRadius: 6, display: "flex", alignItems: "center" }}
                      >
                        Tags
                      </Button>
                    </Popover>
                  </div>
                )}
                <CKEditor
                  editor={ClassicEditor as any}
                  config={{
                    initialData: form.getFieldValue("message") || "",
                  }}
                  onReady={(editor: any) => {
                    setEditorInstance(editor);
                  }}
                  onChange={(event: any, editor: any) => {
                    form.setFieldsValue({ message: editor.getData() });
                  }}
                />
              </div>
            </Form.Item>

            <Divider style={{ margin: "24px 0" }} />

            {/* Attachments */}
            <Row gutter={16}>
              <Col span={24}>
                <Text strong style={{ display: "block", marginBottom: 12 }}>
                  Attachments
                </Text>

                {/* Existing Attachments */}
                {existingAttachments.length > 0 && (
                  <Card size="small" style={{ marginBottom: 16, backgroundColor: "#fafafa" }}>
                    <Space direction="vertical" style={{ width: "100%" }} size={8}>
                      {existingAttachments.map((file) => (
                        <div
                          key={file.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "6px 12px",
                            backgroundColor: "#fff",
                            border: "1px solid #f0f0f0",
                            borderRadius: 6,
                          }}
                        >
                          <Space size={8}>
                            <PaperClipOutlined style={{ color: "#8c8c8c" }} />
                            <Text ellipsis style={{ maxWidth: 450 }}>
                              {file.name}
                            </Text>
                          </Space>
                          <Space>
                            <Tooltip title="Download">
                              <Button
                                type="text"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownloadAttachment(file.id)}
                              />
                            </Tooltip>
                            <Tooltip title="Delete">
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteExistingAttachment(file.id)}
                              />
                            </Tooltip>
                          </Space>
                        </div>
                      ))}
                    </Space>
                  </Card>
                )}

                {/* Newly added files */}
                {newFiles.length > 0 && (
                  <Card size="small" style={{ marginBottom: 16, backgroundColor: "#fafafa" }}>
                    <Space direction="vertical" style={{ width: "100%" }} size={8}>
                      {newFiles.map((file, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "6px 12px",
                            backgroundColor: "#fff",
                            border: "1px solid #f0f0f0",
                            borderRadius: 6,
                          }}
                        >
                          <Space size={8}>
                            <PaperClipOutlined style={{ color: "#1890ff" }} />
                            <Text ellipsis style={{ maxWidth: 450 }}>
                              {file.name} <Text type="secondary" style={{ fontSize: 12 }}>(New)</Text>
                            </Text>
                          </Space>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemoveNewFile(index)}
                          />
                        </div>
                      ))}
                    </Space>
                  </Card>
                )}

                <Upload beforeUpload={handleUpload} showUploadList={false} multiple>
                  <Button icon={<PlusOutlined />} style={{ borderRadius: 6 }}>
                    Select or drop files
                  </Button>
                </Upload>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </Drawer>
  );
};

export default TicketAlertTemplatesDetailsPanel;
